/** Hosts where Curator TV lives at the site root (no /apple prefix). */
import {
  buildCuratorFilmsPath,
  readCachedCuratorUsername,
} from "../shared/curatorUserFilmsProfile";

export const APPLE_PRIMARY_HOSTS = [
  "curatorwatchlist.com",
  "www.curatorwatchlist.com",
];

const LEGACY_APPLE_PREFIX = "/apple";

function readHostname() {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").toLowerCase();
}

/** True on curatorwatchlist.com — Apple TV routes omit the /apple prefix. */
export function isApplePrimaryHost(hostname = readHostname()) {
  const host = String(hostname || "").toLowerCase();
  if (APPLE_PRIMARY_HOSTS.includes(host)) return true;
  const extra = String(process.env.REACT_APP_APPLE_PRIMARY_HOST || "").trim().toLowerCase();
  return extra.length > 0 && host === extra;
}

/** Route prefix: "" on curatorwatchlist.com, "/apple" elsewhere. */
export function getAppleBasePath() {
  return isApplePrimaryHost() ? "" : LEGACY_APPLE_PREFIX;
}

/** Build an Apple TV route from a suffix like "/search" or "list/movies". */
export function appleRoute(suffix = "") {
  const base = getAppleBasePath();
  if (!suffix || suffix === "/") return base || "/";
  const normalized = String(suffix).startsWith("/") ? String(suffix) : `/${suffix}`;
  if (!base) return normalized;
  return `${base}${normalized}`;
}

export function appleHomePath() {
  return appleRoute("");
}

export function appleSearchPath() {
  return appleRoute("/search");
}

export function appleLibraryPath(query = "") {
  const q = String(query || "").trim();
  return q ? `${appleRoute("/library")}?${q.replace(/^\?/, "")}` : appleRoute("/library");
}

/** Prefer the sharable /{username}/films URL when we know the curator username. */
export function appleAccountPath() {
  const cached = readCachedCuratorUsername();
  if (cached) return buildCuratorFilmsPath(cached);
  return appleRoute("/account");
}

export function appleTvHomePath() {
  return appleRoute("/tv");
}

export function appleReelsPath() {
  return appleRoute("/reels");
}

export function appleMapPath() {
  return appleRoute("/map");
}

export function appleListPath(listKey) {
  return appleRoute(`/list/${listKey}`);
}

/** Strip /apple prefix so path checks work on both hosts. */
export function toAppleRelativePath(pathname = "") {
  const path = String(pathname || "");
  if (path.startsWith(`${LEGACY_APPLE_PREFIX}/`)) {
    return path.slice(LEGACY_APPLE_PREFIX.length) || "/";
  }
  if (path === LEGACY_APPLE_PREFIX) return "/";
  if (isApplePrimaryHost()) return path || "/";
  return null;
}

export function isAppleSurfacePath(pathname = "") {
  const path = String(pathname || "");
  // Profile UI only — films.txt is plain text from the API, not an Apple surface.
  if (/^\/[a-z0-9_.-]+\/films(?:\/|$)/i.test(path)) return true;
  if (path.startsWith("/list/")) return true;
  if (path.startsWith(LEGACY_APPLE_PREFIX)) return true;
  if (!isApplePrimaryHost()) return false;
  if (path === "/") return true;
  if (path.startsWith("/tv")) return true;
  if (path.startsWith("/reels")) return true;
  if (path.startsWith("/map")) return true;
  if (path.startsWith("/search")) return true;
  if (path.startsWith("/library")) return true;
  if (path.startsWith("/account")) return true;
  if (path.startsWith("/newsroom")) return true;
  if (path.startsWith("/list/")) return true;
  if (path.startsWith("/person/")) return true;
  if (/^\/(movie|tv-show|tv)\/[^/]+\/\d+/.test(path)) return true;
  return false;
}

export function isAppleDetailPath(pathname = "") {
  const relative = toAppleRelativePath(pathname);
  if (!relative) return false;
  return /^\/(movie|tv-show|tv)\/[^/]+\/\d+$/.test(relative);
}

/** Movie home (`/`) or TV home (`/tv`) — immersive chrome like detail. */
export function isAppleHomePath(pathname = "") {
  const relative = toAppleRelativePath(pathname);
  if (!relative) return false;
  return relative === "/" || relative === "/tv";
}

/** Library, account/profile, newsroom, lists, cast & crew — home-style top bar, no side rail. */
export function isAppleSearchPath(pathname = "") {
  const relative = toAppleRelativePath(pathname);
  if (!relative) return false;
  return relative === "/search" || relative.startsWith("/search/");
}

export function isAppleTopChromePath(pathname = "") {
  const path = String(pathname || "");
  // Public profile routes live outside /apple on every host.
  if (/^\/[a-z0-9_.-]+\/films(?:\/|$)/i.test(path)) return true;
  const relative = toAppleRelativePath(path);
  if (!relative) return false;
  if (relative === "/library" || relative.startsWith("/library/")) return true;
  if (relative === "/account" || relative.startsWith("/account/")) return true;
  if (relative === "/newsroom" || relative === "/newsroom/") return true;
  if (isAppleSearchPath(path)) return true;
  if (relative.startsWith("/list/")) return true;
  if (/^\/(movie|tv-show|tv)\/[^/]+\/\d+\/cast-crew\/?$/.test(relative)) return true;
  return false;
}
